/**
 * Cross-page transitions for the multi-page app:
 *
 *  - **Exit (sortante)**: intercept same-origin link clicks, fade the current
 *    `.page` out, then navigate. Gives a smooth hand-off instead of the abrupt
 *    white blink of a hard navigation — most noticeable when entering a game
 *    mode from the home cards.
 *  - **Enter (entrante)**: every destination `.page` fades/rises in on load.
 *
 * The dark, fixed body background is identical across pages, so fading the page
 * content alone reads as one continuous transition — no overlay needed.
 *
 * The home page keeps its own bespoke staggered intro (see HomeCards), so the
 * generic enter animation deliberately skips it.
 */
export default class PageTransition {
	constructor() {
		this.navigating = false;
		this.reduceMotion = window.matchMedia
			&& window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		this.playEnter();
		this.bindExit();

		// Back-forward cache restore. The exit transition leaves the page at
		// `opacity: 0; transform: translateY(-12px)` right before navigating, so the
		// browser freezes it in that hidden state in bfcache. On Back it's restored
		// as-is — a blank page — and DOMContentLoaded does not re-fire, so playEnter
		// never runs again. pageshow(persisted) is the only hook we get: undo the
		// exit styles and clear the in-flight `navigating` latch (otherwise every
		// later link click would be a no-op).
		window.addEventListener("pageshow", (event) => {
			if (event.persisted) this.restoreAfterBfcache();
		});
	}

	restoreAfterBfcache() {
		this.navigating = false;
		const page = document.querySelector(".pages .page");
		if (!page) return;
		if (typeof gsap !== "undefined") gsap.killTweensOf(page);
		page.style.opacity = "";
		page.style.transform = "";
	}

	/**
	 * Reveal the incoming page. The `.page-enter` gate (set in the HTML, styled in
	 * _layout.scss) hides non-home pages on the first paint; we drop it just before
	 * building the tween so GSAP's immediateRender re-hides the element inline in
	 * the same frame — no flash despite removing the CSS gate first.
	 */
	playEnter() {
		const pages = document.querySelector(".pages");
		if (!pages) return;
		const page = pages.querySelector(".page");

		// Home owns its richer intro; nothing to reveal here for it. Also the
		// no-GSAP / reduced-motion safety net that guarantees content is shown.
		if (!page || page.classList.contains("home") || this.reduceMotion || typeof gsap === "undefined") {
			pages.classList.remove("page-enter");
			return;
		}

		pages.classList.remove("page-enter");
		gsap.from(page, {
			opacity: 0, y: 24, duration: 0.5, ease: "power3.out",
			clearProps: "transform,opacity"
		});
	}

	bindExit() {
		document.addEventListener("click", (e) => {
			if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
			const link = e.target.closest("a");
			if (!link || !this.isInternalNav(link)) return;
			e.preventDefault();
			this.exitTo(link.href);
		});
	}

	/** Only same-origin, same-tab, real page navigations get the transition. */
	isInternalNav(link) {
		const href = link.getAttribute("href");
		if (!href || href.startsWith("#")) return false;
		if (link.target && link.target !== "_self") return false;
		if (link.hasAttribute("download")) return false;

		const url = new URL(link.href, window.location.href);
		if (url.origin !== window.location.origin) return false;          // external / mailto / tel
		if (url.href === window.location.href) return false;              // same page
		if (url.pathname === window.location.pathname && url.hash) return false; // in-page anchor
		return true;
	}

	exitTo(url) {
		if (this.navigating) return;
		this.navigating = true;

		const page = document.querySelector(".pages .page");
		if (this.reduceMotion || typeof gsap === "undefined" || !page) {
			window.location.href = url;
			return;
		}

		const go = () => { window.location.href = url; };
		// Fail-safe: navigate even if the tween's onComplete never fires.
		const fallback = setTimeout(go, 600);
		gsap.to(page, {
			opacity: 0, y: -12, duration: 0.28, ease: "power2.in",
			onComplete: () => { clearTimeout(fallback); go(); }
		});
	}
}
